import { useState } from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import { PlayAnimated, playAnimation } from 'components/icons'
import dynamic from 'next/dynamic'
import Repository from 'icons/repository'
import { Typography } from 'common/typography'
import { SecondaryButton, NeutralButtonOutline } from 'common/button'
import ContentRenderer from 'content-renderer'
import LearningPlanTag from 'components/learning-plan/learning-plan-tag'
import useNearScreen from 'hooks/use-near-screen'
import ProjectStatus from 'student/projects/project-status'
import ProjectRequirements from './project-requirements'
import RevisionContainer from './revision-container'

const VideoCardLazy = dynamic(
  () => import('../../course/video-card-lazy'),
  {
    loading: () => (<div />),
  },
)

const PlanProjectItemStyled = styled.div`
  .project-content {
    flex-direction: column;
    display: flex;
    gap: 1rem;
  }
  .project-preview {
    border-radius: 8px;
    overflow: hidden;
    height: fit-content;
  }
  .video-thumb {
    position: relative;
    &:hover {
      ${playAnimation}
    }
  }
  .thumb {
    width: 100%;
    height: auto;
    object-fit: cover;
  }
  h4 {
    margin-block-end: .5rem;
  }
  .project-description {
    margin-block-start: .5rem;
    margin-block-end: 1.5rem;
  }
  .project-description p, .project-description li {
    color: var(--neutral40);
    margin-block-end: .5rem;
    margin-block-start: .5rem;
  }
  .cta-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .play-container {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      transform: scale(0.7);
    }
  }
  .project-status-styled {
    animation-name: none;
    opacity: 1;
    background: linear-gradient(180deg,rgba(255,255,255,0) -60.31%,rgba(65,62,84,0.5) 50.27%,rgba(4,0,30,0.9) 85.78%);
  }
  @media (min-width: 768px) {
    .cta-container {
      flex-direction: row;
    }
  }
  @media (min-width: 1024px) {
    .project-preview {
      min-inline-size: 384px;
    }
  }
  @media (min-width: 1100px) {
    .thumb {
      width: 384px;
      height: 220px;
    }
    .project-content {
      flex-direction: row;
      gap: 40px;
    }
  }
`

function PlanProjectItem({
  title, slug, video, thumb_image, level, is_free, description, requirements_junior, requirements_mid, requirements_senior, progress_status, refetch,
}) {
  const [show, element] = useNearScreen({ threshold: 1.0 })
  const [open, setOpen] = useState(false)
  const [playing, setPlaying] = useState()

  const has_requirements = requirements_junior || requirements_mid || requirements_senior

  return (
    <PlanProjectItemStyled ref={element}>
      <div className="project-content">
        <div className="project-preview">
          {!playing
            ? (
              <div className="video-thumb" id="video-card-hover" onClick={() => setPlaying(true)}>
                <ProjectStatus show={show} {...progress_status} />
                <img
                  width="384"
                  height="220"
                  className="thumb"
                  src={thumb_image}
                  alt="thumbnail del video de la presentacion del curso"
                  loading="lazy"
                />
                <div className="play-container">
                  <PlayAnimated width="45" height="45" color="white" />
                </div>
              </div>)
            : (
              <VideoCardLazy videoUrl={video.player_url} />
            )}
        </div>
        <div className="project-details">
          <Typography as="h4" font="headline6">
            {title}
          </Typography>
          <LearningPlanTag level={level} free={is_free} notShow />
          <div className="project-description">
            <ContentRenderer chunks={description?.blocks} />
          </div>
          {!progress_status ? (
            <Link href={`/proyectos/${slug}`}>
              <SecondaryButton>
                Ver proyecto
              </SecondaryButton>
            </Link>
          ) : (
            <div className="cta-container">
              <SecondaryButton onClick={() => setOpen(true)}>
                {progress_status.status === 'IN REVIEW' || progress_status.status === 'COMPLETED' ? (
                  'Actualizar repositorio'
                ) : (
                  <>
                    <Repository margin="0 8px 0 0" color="var(--white)" />
                    Vincular repositorio
                  </>
                )}
              </SecondaryButton>
              <Link href={`/proyectos/${slug}`} passHref>
                <NeutralButtonOutline redesign>
                  Ver proyecto
                </NeutralButtonOutline>
              </Link>
            </div>
          )}
        </div>
      </div>
      {has_requirements ? (
        <ProjectRequirements
          junior={requirements_junior?.replaceAll('\\n', '\n')}
          mid={requirements_mid?.replaceAll('\\n', '\n')}
          senior={requirements_senior?.replaceAll('\\n', '\n')}
        />
      ) : null}
      <RevisionContainer
        project={slug}
        open={open}
        setOpen={setOpen}
        progress_status={progress_status}
        refetch={refetch}
        requirements={{
          junior: requirements_junior,
          mid: requirements_mid,
          senior: requirements_senior,
        }}
      />
    </PlanProjectItemStyled>
  )
}

export default PlanProjectItem
